import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PageHeader from '../components/PageHeader';
import FormSection from '../components/FormSection';
import { useAuth } from '../contexts/AuthContext';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const { toast } = useFeedback();
  const [first, setFirst] = useState(user?.first_name || '');
  const [last, setLast] = useState(user?.last_name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  if (!user) return null;

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileError(null);
    try {
      await api.patch(`/users/${user.id}`, { first_name: first, last_name: last, phone });
      await refreshUser();
      toast({ message: 'Profile updated', severity: 'success' });
    } catch (err) {
      setProfileError(apiErrorMessage(err, 'Could not update profile.'));
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next !== confirmPw) {
      setPwError('New passwords do not match.');
      return;
    }
    setSavingPw(true);
    setPwError(null);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      setCurrent('');
      setNext('');
      setConfirmPw('');
      toast({ message: 'Password changed', severity: 'success' });
    } catch (err) {
      setPwError(apiErrorMessage(err, 'Could not change password.'));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <>
      <PageHeader title="Profile" subtitle="Update your personal info and password." />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormSection title="Personal info" description="Used in the app and emails sent to your contacts.">
            <form onSubmit={saveProfile} aria-label="Profile form">
              {profileError && <Alert severity="error" sx={{ mb: 2 }}>{profileError}</Alert>}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField label="First name" value={first} onChange={(e) => setFirst(e.target.value)} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Last name" value={last} onChange={(e) => setLast(e.target.value)} required />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Email" value={user.email} disabled />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Role" value={user.role} disabled />
                </Grid>
              </Grid>
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button type="submit" variant="contained" disabled={savingProfile} aria-label="Save profile">
                  {savingProfile ? 'Saving…' : 'Save changes'}
                </Button>
              </Stack>
            </form>
          </FormSection>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <FormSection title="Change password">
            <form onSubmit={changePassword} aria-label="Change password form">
              {pwError && <Alert severity="error" sx={{ mb: 2 }}>{pwError}</Alert>}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Current password"
                    type="password"
                    autoComplete="current-password"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="New password"
                    type="password"
                    autoComplete="new-password"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Confirm password"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    required
                  />
                </Grid>
              </Grid>
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button type="submit" variant="contained" disabled={savingPw} aria-label="Change password">
                  {savingPw ? 'Saving…' : 'Change password'}
                </Button>
              </Stack>
            </form>
          </FormSection>
          <Typography variant="caption" color="text.secondary">
            Tip: pick something at least 8 characters long.
          </Typography>
        </CardContent>
      </Card>
    </>
  );
}
